"use client";

import { useEffect, useRef, useState, type FormEvent } from "react";
import { useTranslations } from "next-intl";

import { BOTON_PRIMARIO } from "@/components/shared/estilos";
import { IconoSobre } from "@/components/shared/iconos";
import {
  AccesoApiError,
  solicitarCodigo,
  verificarCodigo,
  type CodigoVerificado,
} from "@/lib/acceso";

import CodigoSeisCifras from "./CodigoSeisCifras";
import Spinner from "./Spinner";
import { ENLACE_TACTIL } from "./estilosAcceso";

/** Segundos hasta poder pedir otro código. El backend admite 3 cada 10 minutos. */
const ESPERA_REENVIO_S = 30;
/** Pasado este tiempo verificando, lo más probable es que se esté creando el local. */
const AVISO_CREANDO_MS = 1500;
const DURACION_REENVIADO_MS = 4000;

type Estado = "listo" | "verificando" | "error" | "agotado";

interface PasoCodigoProps {
  /**
   * `correo`: el código que llegó por email (S1-b). `dosFactores`: el de la
   * app de autenticación, que se manda junto al del correo (S1-c).
   */
  tipo: "correo" | "dosFactores";
  email: string;
  /** El código del correo ya aceptado; solo en `dosFactores`. */
  codigoCorreo?: string;
  idioma?: "es" | "en" | "de";
  /** País de la pantalla de acceso (spec 022 §21.4); se guarda si se crea el local. */
  pais?: string | null;
  /** El token de `/start?invite=` (030-lite). */
  invitacion?: string | null;
  /** «Cambiar el correo» (S1-b) o «Volver» (S1-c): de vuelta a S1. */
  onVolver: () => void;
  onDosFactores?: (codigoCorreo: string) => void;
  /** Tras entrar; la página navega y este paso se queda cargando. */
  onEntrar: (resultado: CodigoVerificado) => Promise<void>;
}

/**
 * El paso del código (UI spec 003, S1-b) y el del segundo factor (S1-c), que
 * comparten composición: casilla del icono, titular, ayuda, las seis cifras,
 * línea de estado y «Entrar».
 */
export default function PasoCodigo({
  tipo,
  email,
  codigoCorreo,
  idioma,
  pais,
  invitacion,
  onVolver,
  onDosFactores,
  onEntrar,
}: PasoCodigoProps) {
  const t = useTranslations("auth.acceso");
  const tLogin = useTranslations("auth.login");
  const tErrors = useTranslations("errors");

  const [codigo, setCodigo] = useState("");
  const [estado, setEstado] = useState<Estado>("listo");
  const [mensajeError, setMensajeError] = useState<string | null>(null);
  const [creando, setCreando] = useState(false);
  const [segundos, setSegundos] = useState(tipo === "correo" ? ESPERA_REENVIO_S : 0);
  const [reenviando, setReenviando] = useState(false);
  const [reenviado, setReenviado] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const idCampo = `acceso-codigo-${tipo}`;
  const idEstado = `${idCampo}-estado`;
  const idAyuda = `${idCampo}-ayuda`;

  // La cuenta atrás no se anuncia: solo cambia el texto. El botón aparece en
  // una región `aria-live`, que es lo único que se lee.
  useEffect(() => {
    if (segundos <= 0) return;
    const id = window.setTimeout(() => setSegundos((s) => s - 1), 1000);
    return () => window.clearTimeout(id);
  }, [segundos]);

  useEffect(() => {
    if (!reenviado) return;
    const id = window.setTimeout(() => setReenviado(false), DURACION_REENVIADO_MS);
    return () => window.clearTimeout(id);
  }, [reenviado]);

  function fallar(mensaje: string, siguiente: Estado = "error"): void {
    setEstado(siguiente);
    setMensajeError(mensaje);
    setCodigo("");
    // El input se desactiva mientras verifica; se enfoca cuando vuelve a estar
    // activo, en el siguiente pintado.
    window.requestAnimationFrame(() => inputRef.current?.focus());
  }

  async function verificar(valor: string): Promise<void> {
    if (valor.length !== 6 || estado === "verificando" || estado === "agotado") return;
    setEstado("verificando");
    setMensajeError(null);
    setReenviado(false);
    const reloj =
      tipo === "correo" ? window.setTimeout(() => setCreando(true), AVISO_CREANDO_MS) : undefined;
    try {
      const resultado = await verificarCodigo(
        tipo === "correo"
          ? { email, codigo: valor, idioma, pais, invitacion }
          : { email, codigo: codigoCorreo ?? "", idioma, twoFactorCode: valor, pais, invitacion }
      );
      // Se queda en «verificando» mientras la página se va.
      await onEntrar(resultado);
    } catch (err) {
      window.clearTimeout(reloj);
      setCreando(false);
      if (!(err instanceof AccesoApiError)) {
        fallar(tLogin("errors.connectionFailed"));
        return;
      }
      switch (err.code) {
        case "TWO_FACTOR_REQUIRED":
          if (tipo === "correo") {
            setEstado("listo");
            onDosFactores?.(valor);
          } else {
            fallar(tErrors("TWO_FACTOR_INVALID_CODE"));
          }
          return;
        case "CODIGO_ACCESO_INVALIDO":
          fallar(t("errores.codigoIncorrecto"));
          return;
        case "CODIGO_ACCESO_CADUCADO":
          // Sin cuenta atrás: lo que toca es pedir otro ya.
          setSegundos(0);
          fallar(t("errores.codigoCaducado"));
          return;
        case "CODIGO_ACCESO_AGOTADO":
          setSegundos(0);
          fallar(t("errores.demasiadosIntentos"), "agotado");
          return;
        case "RATE_LIMITED":
          fallar(t("errores.demasiadosCodigos"));
          return;
        case "ACCOUNT_SUSPENDED":
          fallar(tLogin("errors.suspended"));
          return;
        case "EMAIL_DISPOSABLE":
          fallar(t("errores.correoTemporal"), "agotado");
          return;
        case "SIGNUP_RESTRICTED":
          fallar(t("errores.altaRestringida"), "agotado");
          return;
        case "SIGNUP_INVITATION_INVALID":
          fallar(t("errores.invitacionInvalida"), "agotado");
          return;
        case "SIGNUP_INVITATION_EMAIL_MISMATCH":
          fallar(t("errores.invitacionOtroCorreo"), "agotado");
          return;
        default:
          fallar(tLogin("errors.generic"));
      }
    }
  }

  async function reenviar(): Promise<void> {
    setReenviando(true);
    setMensajeError(null);
    try {
      await solicitarCodigo(email, idioma, invitacion);
      setCodigo("");
      setEstado("listo");
      setReenviado(true);
      setSegundos(ESPERA_REENVIO_S);
      window.requestAnimationFrame(() => inputRef.current?.focus());
    } catch (err) {
      const mensaje =
        err instanceof AccesoApiError
          ? err.code === "RATE_LIMITED"
            ? t("errores.demasiadosCodigos")
            : err.code === "EMAIL_DISPOSABLE"
              ? t("errores.correoTemporal")
              : err.code === "SIGNUP_RESTRICTED"
                ? t("errores.altaRestringida")
                : tLogin("errors.generic")
          : tLogin("errors.connectionFailed");
      setEstado((e) => (e === "agotado" ? e : "error"));
      setMensajeError(mensaje);
    } finally {
      setReenviando(false);
    }
  }

  function cambiar(valor: string): void {
    setCodigo(valor);
    if (estado === "error") {
      setEstado("listo");
      setMensajeError(null);
    }
  }

  function enviar(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    void verificar(codigo);
  }

  const verificando = estado === "verificando";
  const esCorreo = tipo === "correo";

  return (
    <form onSubmit={enviar} noValidate className="flex flex-col items-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-surface-accent text-brand">
        <IconoSobre tamano={32} />
      </div>

      <h1 className="mt-4 text-center text-2xl font-bold tracking-tight text-balance text-zinc-950 sm:text-3xl">
        {esCorreo ? t("codigo.titulo") : tLogin("twoFactor.title")}
      </h1>

      <p id={idAyuda} className="mt-3 text-center text-base text-zinc-600">
        {esCorreo
          ? t.rich("codigo.ayuda", {
              email,
              b: (chunks) => <span className="font-semibold break-all text-zinc-900">{chunks}</span>,
            })
          : tLogin("twoFactor.subtitle")}
      </p>
      {esCorreo && (
        <button type="button" onClick={onVolver} className={[ENLACE_TACTIL, "mt-2"].join(" ")}>
          {t("codigo.cambiarCorreo")}
        </button>
      )}

      <div className="mt-6 w-full">
        <CodigoSeisCifras
          id={idCampo}
          valor={codigo}
          onCambio={cambiar}
          onCompleto={(v) => void verificar(v)}
          etiqueta={esCorreo ? t("codigo.label") : tLogin("twoFactor.title")}
          describedBy={`${idEstado} ${idAyuda}`}
          deshabilitado={verificando || estado === "agotado"}
          error={estado === "error" || estado === "agotado"}
          autoFocus
          inputRef={inputRef}
        />
      </div>

      <p
        id={idEstado}
        aria-live="polite"
        className={[
          "mt-3 min-h-5 text-center text-sm",
          mensajeError ? "text-red-700" : "text-zinc-500",
        ].join(" ")}
      >
        {verificando
          ? creando
            ? t("creando")
            : t("codigo.verificando")
          : mensajeError}
      </p>

      <button
        type="submit"
        disabled={codigo.length !== 6 || verificando || estado === "agotado"}
        className={[BOTON_PRIMARIO, "mt-4 w-full gap-2"].join(" ")}
      >
        {verificando && <Spinner />}
        {esCorreo ? t("codigo.entrar") : tLogin("twoFactor.verifyButton")}
      </button>

      {esCorreo ? (
        <>
          <div className="mt-4 text-center text-sm">
            {segundos > 0 && (
              <p className="text-zinc-500">{t("codigo.reenviarEn", { segundos })}</p>
            )}
            <div aria-live="polite">
              {segundos <= 0 && (
                <button
                  type="button"
                  onClick={() => void reenviar()}
                  disabled={reenviando || verificando}
                  className={[ENLACE_TACTIL, "gap-2 disabled:opacity-60"].join(" ")}
                >
                  {reenviando && <Spinner />}
                  {t("codigo.reenviar")}
                </button>
              )}
              {reenviado && <p className="mt-1 text-emerald-700">{t("codigo.reenviado")}</p>}
            </div>
          </div>
          <p className="mt-3 text-center text-xs text-zinc-500">{t("codigo.pista")}</p>
        </>
      ) : (
        <button type="button" onClick={onVolver} className={[ENLACE_TACTIL, "mt-4"].join(" ")}>
          {tLogin("twoFactor.back")}
        </button>
      )}
    </form>
  );
}
